import React, { useEffect, useState, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import copy from "copy-to-clipboard";
import axios from "axios";
import { toast } from "react-toastify";
import endPoint from "../redux/EndPoint/endpoint";
import {
  FaCopy,
  FaCheck,
  FaClock,
  FaSyncAlt,
  FaShieldAlt,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { IoClose } from "react-icons/io5";

const NeoPaymentModal = ({
  isOpen,
  onClose,
  paymentData,
  guestEmail,
  onSuccess,
  onFailure,
}) => {
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedOrderId, setCopiedOrderId] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const upiLink = paymentData?.intentUrl || paymentData?.paymentUrl || "";
  const orderId = paymentData?.orderId || "";
  const amount = paymentData?.amount || "0.00";

  // Reset timer when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeLeft(600);
      setCopiedLink(false);
      setCopiedOrderId(false);
      setIsVerifying(false);
    }
  }, [isOpen]);

  // Countdown timer effect
  useEffect(() => {
    if (!isOpen) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen]);

  // Format seconds into MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Status check function (used for polling & manual check)
  const verifyPaymentStatus = useCallback(
    async (isManual = false) => {
      if (!orderId) return;

      if (isManual) setIsVerifying(true);

      try {
        const storedUuid = localStorage.getItem("uuid");
        const token = localStorage.getItem("loginData");

        const res = await axios.post(
          endPoint.TrackOrder,
          {
            orderId: orderId,
            email: guestEmail || undefined,
          },
          {
            headers: {
              sessionId: storedUuid || "",
              Authorization: token ? `Bearer ${token}` : "",
            },
          }
        );

        const order =
          res.data?.order ||
          (Array.isArray(res.data?.orders) && res.data.orders[0]) ||
          null;

        const pStatus = (order?.paymentStatus || "").toUpperCase();
        const oStatus = (order?.status || "").toUpperCase();

        const successStatuses = [
          "SUCCESS",
          "PAID",
          "CONFIRMED",
          "COMPLETED",
          "PROCESSING",
          "DELIVERED",
        ];
        const failedStatuses = ["FAILED", "CANCELLED", "DECLINED"];

        if (successStatuses.includes(pStatus) || successStatuses.includes(oStatus)) {
          toast.success("Payment verified successfully!");
          if (onSuccess) onSuccess(orderId);
          return;
        }

        if (failedStatuses.includes(pStatus) || failedStatuses.includes(oStatus)) {
          toast.error("Payment failed or cancelled.");
          if (onFailure) onFailure(orderId);
          return;
        }

        if (isManual) {
          toast.info(
            "Payment is still being processed by your bank. Please wait a few seconds and check again."
          );
        }
      } catch (err) {
        console.error("[NeoPayments] Status verification error:", err);
        if (isManual) {
          toast.info("Status check in progress. Please try again shortly.");
        }
      } finally {
        if (isManual) setIsVerifying(false);
      }
    },
    [orderId, guestEmail, onSuccess, onFailure]
  );

  // Automatic polling every 5 seconds while active
  useEffect(() => {
    if (!isOpen || !orderId || timeLeft <= 0) return;

    const pollInterval = setInterval(() => {
      verifyPaymentStatus(false);
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [isOpen, orderId, timeLeft, verifyPaymentStatus]);

  // Copy UPI URL
  const handleCopyLink = () => {
    if (!upiLink) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(upiLink);
    } else {
      copy(upiLink);
    }
    setCopiedLink(true);
    toast.success("UPI Payment link copied!");
    setTimeout(() => setCopiedLink(false), 2500);
  };

  // Copy Order ID
  const handleCopyOrderId = () => {
    if (!orderId) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(orderId);
    } else {
      copy(orderId);
    }
    setCopiedOrderId(true);
    toast.success("Order ID copied!");
    setTimeout(() => setCopiedOrderId(false), 2000);
  };

  // Launch UPI intent
  const handleOpenUpiApp = () => {
    if (upiLink) {
      window.location.href = upiLink;
    } else {
      toast.error("UPI link is not available.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden my-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-4 text-white flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="bg-white text-emerald-700 text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
              UPI Pay
            </span>
            <h3 className="font-semibold text-lg text-white">NeoPayments</h3>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 p-1.5 rounded-full hover:bg-white/10 transition"
            aria-label="Close"
          >
            <IoClose size={22} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Amount & Order details */}
          <div className="text-center pb-4 border-b border-gray-100">
            <span className="text-xs uppercase font-medium text-gray-500 tracking-wider">
              Amount to Pay
            </span>
            <div className="text-3xl font-extrabold text-gray-900 mt-0.5">
              ₹{amount}
            </div>

            <div className="mt-2 flex items-center justify-center space-x-2 text-xs text-gray-500">
              <span>Order ID:</span>
              <span className="font-mono font-semibold text-gray-700">
                {orderId}
              </span>
              <button
                onClick={handleCopyOrderId}
                className="text-emerald-600 hover:text-emerald-700 p-1 transition"
                title="Copy Order ID"
              >
                {copiedOrderId ? <FaCheck size={11} /> : <FaCopy size={11} />}
              </button>
            </div>
          </div>

          {/* Timer */}
          <div className="my-3 flex items-center justify-center">
            <div
              className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                timeLeft < 120
                  ? "bg-red-50 text-red-600 border border-red-200"
                  : "bg-amber-50 text-amber-700 border border-amber-200"
              }`}
            >
              <FaClock size={12} />
              <span>
                {timeLeft > 0
                  ? `QR expires in ${formatTime(timeLeft)}`
                  : "QR code has expired"}
              </span>
            </div>
          </div>

          {/* QR Code Section */}
          <div className="relative my-4 flex flex-col items-center justify-center">
            {timeLeft <= 0 ? (
              <div className="w-[220px] h-[220px] flex flex-col items-center justify-center bg-gray-100 rounded-xl border border-dashed border-gray-300 text-center p-4">
                <FaClock className="text-red-500 text-3xl mb-2" />
                <p className="text-sm font-semibold text-gray-700">
                  QR Code Expired
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Please close and re-initiate payment to generate a new QR.
                </p>
              </div>
            ) : (
              <div className="p-3 bg-white rounded-2xl border-2 border-emerald-100 shadow-md">
                {paymentData?.qrImageUrl ? (
                  <img
                    src={paymentData.qrImageUrl}
                    alt="UPI QR Code"
                    className="w-[210px] h-[210px] object-contain mx-auto"
                  />
                ) : upiLink ? (
                  <QRCodeSVG
                    value={upiLink}
                    size={210}
                    level="M"
                    includeMargin={true}
                  />
                ) : (
                  <div className="w-[210px] h-[210px] flex items-center justify-center text-xs text-gray-400">
                    Generating QR...
                  </div>
                )}
              </div>
            )}

            <p className="mt-3 text-xs font-medium text-gray-600 text-center">
              Scan with any UPI app to pay
            </p>

            {/* Supported App Badges */}
            <div className="flex flex-wrap items-center justify-center gap-1.5 mt-2">
              <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[11px] font-semibold rounded-md border border-blue-100">
                GPay
              </span>
              <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-[11px] font-semibold rounded-md border border-purple-100">
                PhonePe
              </span>
              <span className="px-2 py-0.5 bg-cyan-50 text-cyan-700 text-[11px] font-semibold rounded-md border border-cyan-100">
                Paytm
              </span>
              <span className="px-2 py-0.5 bg-orange-50 text-orange-700 text-[11px] font-semibold rounded-md border border-orange-100">
                BHIM
              </span>
              <span className="px-2 py-0.5 bg-gray-50 text-gray-700 text-[11px] font-semibold rounded-md border border-gray-200">
                CRED / Any UPI
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2.5 mt-5">
            {/* Mobile: Pay using UPI App */}
            {timeLeft > 0 && (
              <button
                onClick={handleOpenUpiApp}
                className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition duration-200 flex items-center justify-center space-x-2 shadow-sm"
              >
                <FaExternalLinkAlt size={14} />
                <span>Pay via UPI App (Mobile)</span>
              </button>
            )}

            {/* Copy UPI Link */}
            {timeLeft > 0 && upiLink && (
              <button
                onClick={handleCopyLink}
                className="w-full py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-sm rounded-xl transition duration-200 flex items-center justify-center space-x-2"
              >
                {copiedLink ? (
                  <>
                    <FaCheck size={13} className="text-emerald-600" />
                    <span className="text-emerald-600 font-semibold">
                      UPI Link Copied!
                    </span>
                  </>
                ) : (
                  <>
                    <FaCopy size={13} />
                    <span>Copy UPI Payment Link</span>
                  </>
                )}
              </button>
            )}

            {/* Check status button */}
            <button
              onClick={() => verifyPaymentStatus(true)}
              disabled={isVerifying}
              className="w-full py-2.5 px-4 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white font-semibold text-sm rounded-xl transition duration-200 flex items-center justify-center space-x-2 shadow-sm"
            >
              <FaSyncAlt
                size={13}
                className={isVerifying ? "animate-spin" : ""}
              />
              <span>
                {isVerifying ? "Verifying Payment..." : "I Have Paid / Check Status"}
              </span>
            </button>
          </div>

          {/* Security & Polling note */}
          <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400">
            <span className="flex items-center space-x-1 text-emerald-700">
              <FaShieldAlt size={11} />
              <span className="font-medium">100% Secure Payment</span>
            </span>
            <span className="flex items-center space-x-1.5 text-gray-500">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span>Auto-detecting payment</span>
            </span>
          </div>

          {/* Cancel button */}
          <div className="mt-3 text-center">
            <button
              onClick={onClose}
              className="text-xs text-gray-500 hover:text-gray-700 underline transition"
            >
              Cancel Payment & Return to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NeoPaymentModal;
